
import {
  formatNumericToString,
  getCanonicalLocales,
  getOperands,
  getOption,
  getType,
  invariant,
  resolveLocale,
  setNumberFormatDigitOptions,
  SupportedLocales
} from './utils.js'
import pluralData from './data/plurals.js'

export default class PluralRules {
  #internal = {}

  /**
   *
   * @param {string | string[] | Locale | undefined} locales
   * @param {Intl.PluralRulesOptions | undefined} options
   */
  constructor (locales, options) {
    const {
      availableLocales,
      relevantExtensionKeys,
      localeData,
      getDefaultLocale
    } = PluralRules

    const requestedLocales = getCanonicalLocales(locales)
    const opt = Object.create(null)
    const opts = options === undefined ? Object.create(null) : Object(options)
    this.#internal.initializedPluralRules = true

    const matcher = getOption(
      opts,
      'localeMatcher',
      'string',
      ['best fit', 'lookup'],
      'best fit'
    )

    opt.localeMatcher = matcher

    this.#internal.type = getOption(
      opts,
      'type',
      'string',
      ['cardinal', 'ordinal'],
      'cardinal'
    )

    setNumberFormatDigitOptions(this.#internal, opts, 0, 3, 'standard')

    const r = resolveLocale(
      availableLocales,
      requestedLocales,
      opt,
      relevantExtensionKeys,
      localeData,
      getDefaultLocale
    )

    this.#internal.locale = r.locale
  }

  resolvedOptions () {
    const opts = Object.create(null)
    const internalSlots = this.#internal
    opts.locale = internalSlots.locale
    opts.type = internalSlots.type

    for (const field of [
      'minimumIntegerDigits',
      'minimumFractionDigits',
      'maximumFractionDigits',
      'minimumSignificantDigits',
      'maximumSignificantDigits'
    ]) {
      const val = internalSlots[field]

      if (val !== undefined) {
        opts[field] = val
      }
    }

    opts.pluralCategories = [
      ...PluralRules.localeData[opts.locale].categories[
        opts.type
      ]
    ]

    return opts
  }

  /**
   *
   * @param {number} val
   * @return {LDMLPluralRule}
   */
  select (val) {
    const pr = this
    const n = Number(val)
    return ResolvePlural(pr, n, { getInternalSlots: () => this.#internal, PluralRuleSelect })
  }

  toString () {
    return '[object PluralRules]'
  }

  /**
   *
   * @param {string | string[] | Locale | undefined} locales
   * @param {Pick<Intl.PluralRulesOptions, 'localeMatcher'> | undefined} options
   */
  static supportedLocalesOf (
    locales,
    options
  ) {
    return SupportedLocales(
      PluralRules.availableLocales,
      getCanonicalLocales(locales),
      options
    )
  }

  /**
   *
   * @param {PluralRulesLocaleData[]} data
   */
  static __addLocaleData (...data) {
    for (const { data: d, locale } of data) {
      PluralRules.localeData[locale] = d
      PluralRules.availableLocales.add(locale)
      if (!PluralRules.__defaultLocale) {
        PluralRules.__defaultLocale = locale
      }
    }
  }

  /** @type {Record<string, PluralRulesData>} */
  static localeData = {}
  static availableLocales = new Set()
  static #defaultLocale = new Intl.DateTimeFormat().resolvedOptions().locale

  static getDefaultLocale () {
    return this.#defaultLocale
  }

  static relevantExtensionKeys = []
  static polyfilled = true
}

for (const locale in pluralData) {
  PluralRules.localeData[locale] = pluralData[locale]
  PluralRules.availableLocales.add(locale)
}

/**
 * http://ecma-international.org/ecma-402/7.0/index.html#sec-pluralruleselect
 * @param {string} locale
 * @param {'cardinal' | 'ordinal'} type
 * @param {number} _n
 * @param {OperandsRecord} param3
 * @return {LDMLPluralRule}
 */
function PluralRuleSelect (
  locale,
  type,
  _n,
  { IntegerDigits, NumberOfFractionDigits, FractionDigits }
) {
  return PluralRules.localeData[locale].fn(
    NumberOfFractionDigits
      ? `${IntegerDigits}.${FractionDigits}`
      : IntegerDigits,
    type === 'ordinal'
  )
}

/**
 * http://ecma-international.org/ecma-402/7.0/index.html#sec-resolveplural
 * @param pl
 * @param n
 * @param PluralRuleSelect Has to pass in bc it's implementation-specific
 */
function ResolvePlural (
  pl,
  n,
  {
    getInternalSlots,
    PluralRuleSelect
  }
) {
  const internalSlots = getInternalSlots(pl)
  invariant(getType(internalSlots) === 'Object', 'pl has to be an object')
  invariant(
    'initializedPluralRules' in internalSlots,
    'pluralrules must be initialized'
  )
  invariant(getType(n) === 'Number', 'n must be a number')
  if (!isFinite(n)) {
    return 'other'
  }
  const { locale, type } = internalSlots
  const res = formatNumericToString(internalSlots, n)
  const s = res.formattedString
  const operands = getOperands(s)
  return PluralRuleSelect(locale, type, n, operands)
}
